import {
  WidgetDrilldownConfig,
  WidgetDrilldownTarget,
} from 'src/app/models/overview-dashboard.model';

/**
 * Parses and resolves the backend-owned drilldown capability carried in a widget's
 * `configJson.drilldown`. Capability (does a drilldown exist + where does it point) is
 * the ONLY thing the backend declares; availability (has-data) and Customize mode are
 * decided by the component. See docs/WIDGET-DRILLDOWN-CONTRACT.md.
 */

const VALID_TARGETS = new Set<WidgetDrilldownTarget>([
  'expenses',
  'accounts-payable',
  'repairs-and-maintenance',
  'vendors',
]);

/** FE route + list storageKey for each semantic target. `vendors` navigates plainly. */
export type ResolvedDrilldownRoute =
  | {
      route: string;
      storageKey: 'expenses' | 'accounts-payable' | 'repairs-and-maintenance';
    }
  | { route: string; plain: true };

const TARGET_ROUTES: Record<WidgetDrilldownTarget, ResolvedDrilldownRoute> = {
  expenses: { route: '/expenses/list', storageKey: 'expenses' },
  'accounts-payable': {
    route: '/accounts-payable/list',
    storageKey: 'accounts-payable',
  },
  'repairs-and-maintenance': {
    route: '/repairs-and-maintenance/list',
    storageKey: 'repairs-and-maintenance',
  },
  vendors: { route: '/vendors/list', plain: true },
};

/** Map a semantic target to its FE route (owned by the FE, never sent by the backend). */
export function resolveDrilldownRoute(
  target: WidgetDrilldownTarget
): ResolvedDrilldownRoute {
  return TARGET_ROUTES[target];
}

/**
 * Widgets/todos land on the lightweight generic listing page (search + default columns)
 * instead of the full, tab-heavy list page. Every dashboard drilldown path — the
 * config-driven one above and the legacy hardcoded fallback map in
 * overview-dashboard.component.ts alike — resolves a `/…/list` route string and funnels
 * through `navigateToListFromDashboard`, so the swap is centralized there rather than at
 * each of the ~40 call sites that still spell out the literal route.
 */
// Vendors is intentionally excluded: `/vendors/list` already narrows to the drilldown's
// specific vendor ids in `dashboardRedirect` mode (see vender-list-view.component.ts), and
// has no tabs/heavy filters to hide — so it keeps going straight there.
const DASHBOARD_LIST_TO_OVERVIEW_ROUTE: Record<string, string> = {
  '/expenses/list': '/expenses/overview',
  '/accounts-payable/list': '/accounts-payable/overview',
  '/repairs-and-maintenance/list': '/repairs-and-maintenance/overview',
};

export function remapDashboardListRouteToOverview(route: string): string {
  return DASHBOARD_LIST_TO_OVERVIEW_ROUTE[route] ?? route;
}

/** Lightweight generic-listing routes used by dashboard widget/todo drilldowns. */
export const DASHBOARD_GENERIC_LISTING_ROUTE_PATHS = [
  'expenses/overview',
  'accounts-payable/overview',
  'repairs-and-maintenance/overview',
] as const;

export function normalizeDashboardRoutePath(url: string): string {
  const raw = url.split('?')[0].split('#')[0].trim();
  return raw.replace(/^\/+/, '').toLowerCase();
}

export function isDashboardGenericListingRoute(url: string): boolean {
  const path = normalizeDashboardRoutePath(url);
  return (DASHBOARD_GENERIC_LISTING_ROUTE_PATHS as readonly string[]).includes(
    path
  );
}

export function isDashboardRedirectNavigation(url: string): boolean {
  try {
    const query = url.includes('?') ? url.split('?')[1].split('#')[0] : '';
    return new URLSearchParams(query).get('dashboardRedirect') === '1';
  } catch {
    return false;
  }
}

/**
 * Temporary until BE pagePermissions include `/…/overview`.
 * Dashboard widgets open these routes with `?dashboardRedirect=1`.
 */
export function isTemporaryDashboardOverviewNavigation(url: string): boolean {
  return (
    isDashboardGenericListingRoute(url) && isDashboardRedirectNavigation(url)
  );
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Extract a `WidgetDrilldownConfig` from a widget's raw `configJson` string, or `null`
 * when there is no valid drilldown declared. Never throws on malformed JSON.
 */
export function parseWidgetDrilldownConfig(
  configJson: string | null | undefined
): WidgetDrilldownConfig | null {
  const raw = (configJson ?? '').trim();
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isPlainObject(parsed) || !isPlainObject(parsed['drilldown'])) {
    return null;
  }
  const d = parsed['drilldown'] as Record<string, unknown>;

  const target = typeof d['target'] === 'string' ? (d['target'] as string) : '';
  if (!VALID_TARGETS.has(target as WidgetDrilldownTarget)) {
    // A drilldown with no recognized target is not actionable — treat as "not configured".
    return null;
  }

  const config: WidgetDrilldownConfig = {
    target: target as WidgetDrilldownTarget,
  };

  if (typeof d['context'] === 'string' && (d['context'] as string).trim()) {
    config.context = (d['context'] as string).trim();
  }
  if (isPlainObject(d['filters'])) {
    config.filters = d['filters'] as Record<string, unknown>;
  }
  if (isPlainObject(d['row'])) {
    const row = d['row'] as Record<string, unknown>;
    const contextField =
      typeof row['contextField'] === 'string'
        ? (row['contextField'] as string).trim()
        : '';
    if (contextField) {
      config.row = {
        contextField,
        enabledField:
          typeof row['enabledField'] === 'string' && (row['enabledField'] as string).trim()
            ? (row['enabledField'] as string).trim()
            : undefined,
      };
    }
  }

  return config;
}
